import { useCallback, useState } from "react"

import { IconDownload } from "@tabler/icons-react"
import { Column, ColumnMeta } from "@tanstack/react-table"
import { ColumnOption, stringify } from "csv-stringify/browser/esm/sync"

import { downloadFile } from "@/lib/helpers/fileDownload"

import { Button } from "../../Button"
import { useToast } from "../../Toast"
import { Tooltip } from "../../Tooltip"
import { KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID } from "../DataTable"
import { LE_TableInstance } from "../types"

const EXPORT_BUTTON_ID_SUFFIX = "export-button"

export function ExportButton<T extends Record<string, any> = object>({
  table,
  data,
  exportFilename,
}: {
  table: LE_TableInstance<T>
  data: T[]
  exportFilename?: string
}) {
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()

  const columns = table.getAllFlatColumns()
  const visibleColumnIds = table.getVisibleFlatColumns().map((column) => column.id)

  const handleExport = useCallback(async () => {
    const formatters: Record<string, ColumnMeta<T, any>["exportFormatter"]> = {}
    const csvColumns: ColumnOption[] = []

    let exportData: T[] = data

    if (table.getExportRows) {
      const toastId = toast.loading("Generating CSV...")
      setIsLoading(true)

      const promise = table.getExportRows()
      promise
        .catch(() => {
          toast.error("Failed to generate CSV")
        })
        .finally(() => {
          toast.dismiss(toastId)
          setIsLoading(false)
        })
      exportData = await promise
    }

    const shouldExport = (column: Column<T, any>) => {
      const id = column.id
      const metaColumnIds = [SELECTABLE_COLUMN_ID, KEBAB_MENU_COLUMN_ID]

      if (metaColumnIds.includes(id)) {
        return false
      }
      return visibleColumnIds.includes(id)
    }

    const makeDefaultFormatter = (column: Column<T, any>) => {
      const accessor = column.accessorFn
      return accessor ? (row: T) => accessor(row, 0) : null
    }

    for (const column of columns) {
      const formatter =
        column.columnDef.meta?.exportFormatter || makeDefaultFormatter(column)

      if (shouldExport(column) && formatter) {
        const headerLabel =
          typeof column.columnDef.header === "string"
            ? column.columnDef.header
            : column.id
        const unitLabel = column.columnDef.meta?.exportUnitLabel
          ? ` (${column.columnDef.meta.exportUnitLabel})`
          : ``

        csvColumns.push({
          key: column.id,
          header: `${headerLabel}${unitLabel}`,
        })
        formatters[column.id] = formatter
      }
    }

    const formattedData = exportData.map((row) => {
      const formattedRow: Record<string, string | number> = {}
      for (const col of csvColumns) {
        formattedRow[col.key] = formatters[col.key]?.(row) ?? ""
      }
      return formattedRow
    })

    const content = stringify(formattedData, {
      header: true,
      bom: true,
      columns: csvColumns,
    })

    const filename = exportFilename ?? "export"

    downloadFile(
      content,
      filename.concat(
        ` - ${new Intl.DateTimeFormat(undefined, {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        }).format(new Date())}.csv`,
      ),
      "text/csv",
    )
  }, [columns, visibleColumnIds, data, exportFilename, table, toast])

  return (
    <Tooltip content="Download CSV">
      <Button
        id={table.getElementId(EXPORT_BUTTON_ID_SUFFIX)}
        variant="neutral"
        onClick={handleExport}
        icon={IconDownload}
        iconOnly
        className="h-fit"
        loading={isLoading}
      />
    </Tooltip>
  )
}
